import {
  Navigation,
  Menu,
  Zap,
  Columns,
  Grid3X3,
  Megaphone,
  Anchor,
  Images,
  FileText,
  DollarSign,
  Users,
  Video,
  Type,
  Code,
  Settings,
  Layout,
  MapPin,
  HelpCircle,
  MessageSquareQuote,
  Timer,
  Heading,
  Bell,
  Award,
  TrendingUp,
  BookOpen,
  Sparkles,
} from 'lucide-react';
import type { ComponentType } from '../../schemas/validation';

export interface ComponentColorScheme {
  bg: string;
  iconBg: string;
  iconColor: string;
  hoverBg: string;
  hoverBorder: string;
  hoverIcon: string;
  hoverText: string;
  hoverDescription: string;
}

export interface ComponentOption {
  type: ComponentType;
  name: string;
  description: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  category: string;
  colorScheme: ComponentColorScheme;
  /** Only show when the site has a blog installed */
  requiresBlog?: boolean;
  /** Only show when the site owner has linked Compete Studio */
  requiresStudioLink?: boolean;
}

export const componentOptions: ComponentOption[] = [
  {
    type: 'navbar',
    name: 'Navigation Bar',
    description: 'Site navigation with logo and menu links',
    icon: Navigation,
    category: 'Navigation',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      hoverBg: 'hover:bg-blue-25',
      hoverBorder: 'hover:border-blue-200',
      hoverIcon: 'group-hover:text-blue-700',
      hoverText: 'group-hover:text-blue-900',
      hoverDescription: 'group-hover:text-blue-600',
    },
  },
  {
    type: 'hero',
    name: 'Hero Section',
    description: 'Eye-catching header with title and call-to-action',
    icon: Zap,
    category: 'Headers',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      hoverBg: 'hover:bg-purple-25',
      hoverBorder: 'hover:border-purple-200',
      hoverIcon: 'group-hover:text-purple-700',
      hoverText: 'group-hover:text-purple-900',
      hoverDescription: 'group-hover:text-purple-600',
    },
  },
  {
    type: 'split-hero',
    name: 'Split Hero',
    description: 'Header section with image and text side-by-side',
    icon: Columns,
    category: 'Headers',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      hoverBg: 'hover:bg-indigo-25',
      hoverBorder: 'hover:border-indigo-200',
      hoverIcon: 'group-hover:text-indigo-700',
      hoverText: 'group-hover:text-indigo-900',
      hoverDescription: 'group-hover:text-indigo-600',
    },
  },
  {
    type: 'simple-header',
    name: 'Simple Header',
    description: 'Clean H1 header for interior pages',
    icon: Heading,
    category: 'Headers',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-slate-50',
      iconColor: 'text-slate-600',
      hoverBg: 'hover:bg-slate-25',
      hoverBorder: 'hover:border-slate-200',
      hoverIcon: 'group-hover:text-slate-700',
      hoverText: 'group-hover:text-slate-900',
      hoverDescription: 'group-hover:text-slate-600',
    },
  },
  {
    type: 'features',
    name: 'Features Grid',
    description: 'Showcase product features with icons and descriptions',
    icon: Grid3X3,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
      hoverBg: 'hover:bg-green-25',
      hoverBorder: 'hover:border-green-200',
      hoverIcon: 'group-hover:text-green-700',
      hoverText: 'group-hover:text-green-900',
      hoverDescription: 'group-hover:text-green-600',
    },
  },
  {
    type: 'cta',
    name: 'Call to Action',
    description: 'Conversion-focused section with buttons',
    icon: Megaphone,
    category: 'Marketing',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600',
      hoverBg: 'hover:bg-orange-25',
      hoverBorder: 'hover:border-orange-200',
      hoverIcon: 'group-hover:text-orange-700',
      hoverText: 'group-hover:text-orange-900',
      hoverDescription: 'group-hover:text-orange-600',
    },
  },
  {
    type: 'footer',
    name: 'Footer',
    description: 'Site footer with links and contact information',
    icon: Anchor,
    category: 'Navigation',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      hoverBg: 'hover:bg-blue-25',
      hoverBorder: 'hover:border-blue-200',
      hoverIcon: 'group-hover:text-blue-700',
      hoverText: 'group-hover:text-blue-900',
      hoverDescription: 'group-hover:text-blue-600',
    },
  },
  {
    type: 'form',
    name: 'Contact Form',
    description: 'Customizable form with email notifications',
    icon: FileText,
    category: 'Forms',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      hoverBg: 'hover:bg-indigo-25',
      hoverBorder: 'hover:border-indigo-200',
      hoverIcon: 'group-hover:text-indigo-700',
      hoverText: 'group-hover:text-indigo-900',
      hoverDescription: 'group-hover:text-indigo-600',
    },
  },
  {
    type: 'compete-form',
    name: 'Compete Studio Form',
    description: 'Embed a form you already created in Compete Studio',
    icon: FileText,
    category: 'Forms',
    requiresStudioLink: true,
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      hoverBg: 'hover:bg-indigo-25',
      hoverBorder: 'hover:border-indigo-200',
      hoverIcon: 'group-hover:text-indigo-700',
      hoverText: 'group-hover:text-indigo-900',
      hoverDescription: 'group-hover:text-indigo-600',
    },
  },
  {
    type: 'team',
    name: 'Team',
    description: 'Showcase team members with photos and social links',
    icon: Users,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      hoverBg: 'hover:bg-purple-25',
      hoverBorder: 'hover:border-purple-200',
      hoverIcon: 'group-hover:text-purple-700',
      hoverText: 'group-hover:text-purple-900',
      hoverDescription: 'group-hover:text-purple-600',
    },
  },
  {
    type: 'media-text',
    name: 'Media Text',
    description: 'Display media (image or video) alongside text content',
    icon: Images,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      hoverBg: 'hover:bg-teal-25',
      hoverBorder: 'hover:border-teal-200',
      hoverIcon: 'group-hover:text-teal-700',
      hoverText: 'group-hover:text-teal-900',
      hoverDescription: 'group-hover:text-teal-600',
    },
  },
  {
    type: 'pricing',
    name: 'Pricing Table',
    description: 'Product and service pricing with plans and features',
    icon: DollarSign,
    category: 'Marketing',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      hoverBg: 'hover:bg-emerald-25',
      hoverBorder: 'hover:border-emerald-200',
      hoverIcon: 'group-hover:text-emerald-700',
      hoverText: 'group-hover:text-emerald-900',
      hoverDescription: 'group-hover:text-emerald-600',
    },
  },
  {
    type: 'video',
    name: 'Video',
    description: 'Display a video',
    icon: Video,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-pink-50',
      iconColor: 'text-pink-600',
      hoverBg: 'hover:bg-pink-25',
      hoverBorder: 'hover:border-pink-200',
      hoverIcon: 'group-hover:text-pink-700',
      hoverText: 'group-hover:text-pink-900',
      hoverDescription: 'group-hover:text-pink-600',
    },
  },
  {
    type: 'text-blocks',
    name: 'Text Blocks',
    description: 'Add text blocks to your content',
    icon: Type,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-gray-50',
      iconColor: 'text-gray-600',
      hoverBg: 'hover:bg-gray-25',
      hoverBorder: 'hover:border-gray-200',
      hoverIcon: 'group-hover:text-gray-700',
      hoverText: 'group-hover:text-gray-900',
      hoverDescription: 'group-hover:text-gray-600',
    },
  },
  {
    type: 'title-subtitle',
    name: 'Title & Subtitle',
    description: 'Simple title and subtitle text with formatting options',
    icon: FileText,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      hoverBg: 'hover:bg-indigo-25',
      hoverBorder: 'hover:border-indigo-200',
      hoverIcon: 'group-hover:text-indigo-700',
      hoverText: 'group-hover:text-indigo-900',
      hoverDescription: 'group-hover:text-indigo-600',
    },
  },
  {
    type: 'contact-info',
    name: 'Contact Info',
    description: 'Display contact details with map embed in two-column layout',
    icon: MapPin,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      hoverBg: 'hover:bg-emerald-25',
      hoverBorder: 'hover:border-emerald-200',
      hoverIcon: 'group-hover:text-emerald-700',
      hoverText: 'group-hover:text-emerald-900',
      hoverDescription: 'group-hover:text-emerald-600',
    },
  },
  {
    type: 'head-content',
    name: 'SEO & Meta Tags',
    description: 'Manage page meta tags, SEO, and analytics scripts',
    icon: Settings,
    category: 'Technical',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-600',
      hoverBg: 'hover:bg-violet-25',
      hoverBorder: 'hover:border-violet-200',
      hoverIcon: 'group-hover:text-violet-700',
      hoverText: 'group-hover:text-violet-900',
      hoverDescription: 'group-hover:text-violet-600',
    },
  },
  {
    type: 'body-end',
    name: 'Scripts & Widgets',
    description: 'Add chat widgets, analytics, and end-of-body scripts',
    icon: Code,
    category: 'Technical',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      hoverBg: 'hover:bg-amber-25',
      hoverBorder: 'hover:border-amber-200',
      hoverIcon: 'group-hover:text-amber-700',
      hoverText: 'group-hover:text-amber-900',
      hoverDescription: 'group-hover:text-amber-600',
    },
  },
  {
    type: 'html-embed',
    name: 'Embed Content',
    description: 'Embed videos, maps, social media posts, or custom HTML',
    icon: Layout,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
      hoverBg: 'hover:bg-cyan-25',
      hoverBorder: 'hover:border-cyan-200',
      hoverIcon: 'group-hover:text-cyan-700',
      hoverText: 'group-hover:text-cyan-900',
      hoverDescription: 'group-hover:text-cyan-600',
    },
  },
  {
    type: 'faq',
    name: 'FAQ',
    description: 'Frequently asked questions with expandable answers',
    icon: HelpCircle,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
      hoverBg: 'hover:bg-yellow-25',
      hoverBorder: 'hover:border-yellow-200',
      hoverIcon: 'group-hover:text-yellow-700',
      hoverText: 'group-hover:text-yellow-900',
      hoverDescription: 'group-hover:text-yellow-600',
    },
  },
  {
    type: 'testimonials',
    name: 'Testimonials',
    description: 'Customer reviews and testimonials with ratings',
    icon: MessageSquareQuote,
    category: 'Marketing',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      hoverBg: 'hover:bg-amber-25',
      hoverBorder: 'hover:border-amber-200',
      hoverIcon: 'group-hover:text-amber-700',
      hoverText: 'group-hover:text-amber-900',
      hoverDescription: 'group-hover:text-amber-600',
    },
  },
  {
    type: 'countdown-timer',
    name: 'Countdown Timer',
    description: 'Display a countdown timer with customizable styling and expiration handling',
    icon: Timer,
    category: 'Marketing',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
      hoverBg: 'hover:bg-red-25',
      hoverBorder: 'hover:border-red-200',
      hoverIcon: 'group-hover:text-red-700',
      hoverText: 'group-hover:text-red-900',
      hoverDescription: 'group-hover:text-red-600',
    },
  },
  {
    type: 'promo-banner',
    name: 'Promo Banner',
    description: 'Slim announcement bar for promotions, sales, and alerts',
    icon: Bell,
    category: 'Marketing',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-600',
      hoverBg: 'hover:bg-rose-25',
      hoverBorder: 'hover:border-rose-200',
      hoverIcon: 'group-hover:text-rose-700',
      hoverText: 'group-hover:text-rose-900',
      hoverDescription: 'group-hover:text-rose-600',
    },
  },
  {
    type: 'image-gallery',
    name: 'Image Gallery',
    description: 'Display a collection of images with lightbox and filtering',
    icon: Images,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      hoverBg: 'hover:bg-purple-25',
      hoverBorder: 'hover:border-purple-200',
      hoverIcon: 'group-hover:text-purple-700',
      hoverText: 'group-hover:text-purple-900',
      hoverDescription: 'group-hover:text-purple-600',
    },
  },
  {
    type: 'image-info',
    name: 'Image Info',
    description: 'Display images with subtitles and descriptions',
    icon: Images,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      hoverBg: 'hover:bg-blue-25',
      hoverBorder: 'hover:border-blue-200',
      hoverIcon: 'group-hover:text-blue-700',
      hoverText: 'group-hover:text-blue-900',
      hoverDescription: 'group-hover:text-blue-600',
    },
  },
  {
    type: 'image-card',
    name: 'Image Cards',
    description: 'Cards with background images and text overlays',
    icon: Images,
    category: 'Content',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      hoverBg: 'hover:bg-indigo-25',
      hoverBorder: 'hover:border-indigo-200',
      hoverIcon: 'group-hover:text-indigo-700',
      hoverText: 'group-hover:text-indigo-900',
      hoverDescription: 'group-hover:text-indigo-600',
    },
  },
  {
    type: 'navigation-menu',
    name: 'Expandable Navigation Bar',
    description: 'Site navigation with dropdown menus under each header',
    icon: Menu,
    category: 'Premium',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      hoverBg: 'hover:bg-indigo-25',
      hoverBorder: 'hover:border-indigo-200',
      hoverIcon: 'group-hover:text-indigo-700',
      hoverText: 'group-hover:text-indigo-900',
      hoverDescription: 'group-hover:text-indigo-600',
    },
  },
  {
    type: 'logocloud',
    name: 'Logo Cloud',
    description: 'Showcase partner, client, or sponsor logos in a polished grid or row',
    icon: Award,
    category: 'Premium',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      hoverBg: 'hover:bg-amber-25',
      hoverBorder: 'hover:border-amber-200',
      hoverIcon: 'group-hover:text-amber-700',
      hoverText: 'group-hover:text-amber-900',
      hoverDescription: 'group-hover:text-amber-600',
    },
  },
  {
    type: 'stats',
    name: 'Stats Counter',
    description: 'Animated number counters for key metrics and achievements',
    icon: TrendingUp,
    category: 'Premium',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
      hoverBg: 'hover:bg-cyan-25',
      hoverBorder: 'hover:border-cyan-200',
      hoverIcon: 'group-hover:text-cyan-700',
      hoverText: 'group-hover:text-cyan-900',
      hoverDescription: 'group-hover:text-cyan-600',
    },
  },
  {
    type: 'blog-preview',
    name: 'Blog Preview',
    description: 'Show latest blog posts with cards linking to full articles',
    icon: BookOpen,
    category: 'Premium',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-600',
      hoverBg: 'hover:bg-violet-25',
      hoverBorder: 'hover:border-violet-200',
      hoverIcon: 'group-hover:text-violet-700',
      hoverText: 'group-hover:text-violet-900',
      hoverDescription: 'group-hover:text-violet-600',
    },
    requiresBlog: true,
  },
  {
    type: 'form-split',
    name: 'Split Form',
    description: 'Contact form beside image or video — great for landing pages',
    icon: Columns,
    category: 'Premium',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      hoverBg: 'hover:bg-indigo-25',
      hoverBorder: 'hover:border-indigo-200',
      hoverIcon: 'group-hover:text-indigo-700',
      hoverText: 'group-hover:text-indigo-900',
      hoverDescription: 'group-hover:text-indigo-600',
    },
  },
  {
    type: 'html-split',
    name: 'Split HTML',
    description: 'Text and CTAs beside custom HTML, calendars, or embeds',
    icon: Code,
    category: 'Premium',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      hoverBg: 'hover:bg-teal-25',
      hoverBorder: 'hover:border-teal-200',
      hoverIcon: 'group-hover:text-teal-700',
      hoverText: 'group-hover:text-teal-900',
      hoverDescription: 'group-hover:text-teal-600',
    },
  },
  {
    type: 'conversion-popup',
    name: 'Conversion Popup',
    description:
      'Timed, scroll, or exit-intent popup with targeting, session rules, and mobile-friendly layouts',
    icon: Sparkles,
    category: 'Premium',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-fuchsia-50',
      iconColor: 'text-fuchsia-600',
      hoverBg: 'hover:bg-fuchsia-25',
      hoverBorder: 'hover:border-fuchsia-200',
      hoverIcon: 'group-hover:text-fuchsia-700',
      hoverText: 'group-hover:text-fuchsia-900',
      hoverDescription: 'group-hover:text-fuchsia-600',
    },
  },
  {
    type: 'content-area',
    name: 'Content Area',
    description: 'Placeholder for page-specific content in layouts',
    icon: Layout,
    category: 'Layout',
    colorScheme: {
      bg: 'bg-white',
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      hoverBg: 'hover:bg-purple-25',
      hoverBorder: 'hover:border-purple-200',
      hoverIcon: 'group-hover:text-purple-700',
      hoverText: 'group-hover:text-purple-900',
      hoverDescription: 'group-hover:text-purple-600',
    },
  },
];

// Helper functions
export const getComponentIcon = (type: ComponentType) => {
  const option = componentOptions.find(opt => opt.type === type);
  return option?.icon || FileText;
};

export const getComponentColorScheme = (type: ComponentType) => {
  const option = componentOptions.find(opt => opt.type === type);
  return option?.colorScheme || componentOptions[0].colorScheme;
};

// Layout mode components - only these are shown when editing layouts
export const LAYOUT_MODE_COMPONENTS: ComponentType[] = [
  'navbar',
  'navigation-menu',
  'footer',
  'content-area',
];
